import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { machineLocal } from '~/data/local';
import { machineRemote } from '~/data/remote';
import { Issue, Machine, MachineTable, Service } from '~/types';
import { OperatingTimePayload } from '~/machines/store/machine';
import store from '~/store';
import { getFavoriteMachineIds, getLastUsedMachineIds } from '~/sqlite/lastUsedCache';

function cordovaFileBaseDir(): string {
  const dataDirectory = window['cordova']?.file?.dataDirectory;
  return dataDirectory ? `${dataDirectory}/` : '';
}

function mapMachineRow(row: MachineTable): Machine {
  const baseDir = cordovaFileBaseDir();
  return {
    ...JSON.parse(row.data),
    id: row.id,
    categoryImage: row.categoryImage ? `${baseDir}${row.categoryImage}` : '',
  };
}

export class MachineRepository {
  async getMachine(id: Machine['id']): Promise<Machine | undefined> {
    if (connectivityService.isOnline()) {
      const data = await machineRemote.fetchMachine(id);
      if (data && store.getState().machines.isOfflineAvailable(id)) {
        machineLocal.updateMachineDetail(data);
        await machineLocal.upsertIssues((data.issues || []).map(issue => ({
          ...issue,
          machineId: (issue as Issue & { machineId?: string }).machineId || data.id,
        })));
        await machineLocal.upsertServices((data.services || []).map(service => ({
          ...service,
          machineId: (service as Service & { machineId?: string }).machineId || data.id,
        })));
      }
      return data;
    }
    return machineLocal.machineDetails(id);
  }

  async getDashboard(id: Machine['id']): Promise<Machine | undefined> {
    if (connectivityService.isOnline()) {
      const data = await machineRemote.fetchDashboard(id);
      if (store.getState().machines.isOfflineAvailable(id)) {
        await machineLocal.updateMachineDashboardDetail(data);
      }
      return data;
    }
    return machineLocal.machineDetails(id);
  }

  async registerOperatingTime(payload: OperatingTimePayload): Promise<void> {
    if (connectivityService.isOnline()) {
      await machineRemote.registerOperatingTime(payload);
      if (!payload.sync) {
        await this.getMachine(payload.machineId);
      }
      return;
    }
    await machineLocal.registerOperationTimeOrCount(payload);
  }

  async syncFavoritesCache(favorites: Machine[]): Promise<void> {
    let lastUsed: Machine[] = [];
    try {
      lastUsed = await machineRemote.fetchLastUsed();
    } catch {
      lastUsed = [];
    }

    await machineLocal.reset(favorites);
    const favoriteIds = new Set(favorites.map((machine) => String(machine.id)));
    const nonFavoriteLastUsed = lastUsed.filter(
      (machine) => !favoriteIds.has(String(machine.id))
    );
    if (nonFavoriteLastUsed.length) {
      await machineLocal.upsertMachines(nonFavoriteLastUsed);
    }
  }

  async getOfflineFavorites(): Promise<Machine[]> {
    const machines = await machineLocal.getAll();
    if (!machines) {
      return [];
    }
    const favoriteIds = getFavoriteMachineIds();
    const rows =
      favoriteIds === null || favoriteIds.length === 0
        ? []
        : machines.filter((machine) => favoriteIds.includes(String(machine.id)));
    return rows.map(mapMachineRow);
  }

  async getOfflineLastUsed(): Promise<Machine[]> {
    const lastUsedIds = getLastUsedMachineIds();
    const machines = await machineLocal.getAll();
    if (!machines) {
      return [];
    }
    const byId = new Map(machines.map((machine) => [String(machine.id), machine]));
    return lastUsedIds.reduce((list, id) => {
      const machine = byId.get(id);
      if (machine) {
        list.push(mapMachineRow(machine));
      }
      return list;
    }, [] as Machine[]);
  }

  async cacheOpenedMachine(machine: Machine): Promise<void> {
    if (!store.getState().machines.isOfflineAvailable(machine.id)) {
      return;
    }
    await machineLocal.upsertMachines([machine]);
    await machineLocal.upsertIssues(
      (machine.issues || []).map((issue) => ({
        ...issue,
        machineId: (issue as Issue & { machineId?: string }).machineId || machine.id,
      }))
    );
    await machineLocal.upsertServices(
      (machine.services || []).map((service) => ({
        ...service,
        machineId: (service as Service & { machineId?: string }).machineId || machine.id,
      }))
    );
  }
}

export const machineRepository = new MachineRepository();
